import React, { useState, useEffect } from 'react';
import '../styles/pages/AdminApplications.css';

const AdminApplications = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [filterWorkAuth, setFilterWorkAuth] = useState('all');
  const [filterUsCitizen, setFilterUsCitizen] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRangeDays, setDateRangeDays] = useState(3); // Default 3 days
  const [searchAllData, setSearchAllData] = useState(false); // Toggle for search mode
  const [totalApplicationsCount, setTotalApplicationsCount] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    // Check if already authenticated on component mount
    const token = localStorage.getItem('admin_token');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  // Effect for search all data mode with debouncing
  useEffect(() => {
    if (!isAuthenticated) return;
    
    if (searchAllData && searchTerm) {
      // Debounce the search API call
      const debounceTimer = setTimeout(() => {
        const token = localStorage.getItem('admin_token');
        if (token) {
          fetchApplications(token, dateRangeDays, searchTerm);
        }
      }, 500); // 500ms debounce
      
      return () => clearTimeout(debounceTimer);
    } else if (searchAllData && !searchTerm) {
      // If search is cleared while in "search all" mode, fetch with date range
      const token = localStorage.getItem('admin_token');
      if (token) {
        fetchApplications(token, dateRangeDays, '');
      }
    }
  }, [searchTerm, searchAllData, isAuthenticated]);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setIsAuthenticated(true);
        fetchApplications(token);
      } else {
        // Token is invalid, remove it
        localStorage.removeItem('admin_token');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error verifying token:', err);
      localStorage.removeItem('admin_token');
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('admin_token', data.access_token);
        setIsAuthenticated(true);
        setPassword('');
        fetchApplications(data.access_token);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Invalid password');
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Failed to connect to server');
      setLoading(false);
    }
  };

  const fetchApplications = async (token, daysParam = null, searchParam = '') => {
    setLoadingApplications(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      
      // Use provided params or fall back to state
      const days = daysParam !== null ? daysParam : dateRangeDays;
      const search = searchParam || (searchAllData ? searchTerm : '');
      
      // Add days parameter (null means "all time")
      if (days !== null && days !== 'all') {
        params.append('days', days);
      }
      
      // Add search parameter if searching all data
      if (searchAllData && search) {
        params.append('search', search);
      }
      
      const url = `${API_URL}/api/admin/applications${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications);
        setTotalApplicationsCount(data.total_count);
      } else {
        setError('Failed to fetch applications');
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to fetch applications');
    } finally {
      setLoadingApplications(false);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setApplications([]);
    setPassword('');
    setError('');
  };

  const handleDownloadResume = async (applicationId, filename) => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setError('Not authenticated');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/applications/${applicationId}/resume`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Create blob from response
        const blob = await response.blob();
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setError('Failed to download resume');
      }
    } catch (err) {
      console.error('Error downloading resume:', err);
      setError('Failed to download resume');
    }
  };

  const handleToggleVerified = async (applicationId, currentVerified) => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setError('Not authenticated');
      return;
    }

    const newVerified = currentVerified === 1 ? 0 : 1;

    try {
      const response = await fetch(`${API_URL}/api/admin/applications/${applicationId}/verified`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ verified: newVerified }),
      });

      if (response.ok) {
        // Update the local state
        setApplications(prevApps =>
          prevApps.map(app =>
            app.id === applicationId ? { ...app, verified: newVerified } : app
          )
        );
      } else {
        setError('Failed to update verified status');
      }
    } catch (err) {
      console.error('Error updating verified status:', err);
      setError('Failed to update verified status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Format work authorization to short code
  const formatWorkAuth = (workAuth) => {
    if (!workAuth) return 'US CITIZEN';
    
    const workAuthMapping = {
      "CPT EAD (Curricular Practical Training)": "CPT EAD",
      "OPT EAD (Optional Practical Training)": "OPT EAD",
      "H-1B Visa": "H-1B",
      "H-4 EAD": "H-4 EAD",
      "L-1 Visa": "L-1",
      "TN Visa (NAFTA)": "TN",
      "EAD (Green Card in Process)": "EAD GC",
      "Green Card Holder": "GC",
      "Asylum/Refugee EAD": "ASYLUM",
      "Other": "OTHER"
    };
    
    return workAuthMapping[workAuth] || workAuth;
  };

  // Get unique work authorization types
  const getWorkAuthTypes = () => {
    const types = new Set();
    applications.forEach(app => {
      if (app.work_authorization) {
        types.add(app.work_authorization);
      }
    });
    return Array.from(types).sort();
  };

  // Filter applications
  const filteredApplications = applications.filter(app => {
    // Filter by work authorization
    if (filterWorkAuth !== 'all' && app.work_authorization !== filterWorkAuth) {
      return false;
    }
    
    // Filter by US citizen
    if (filterUsCitizen !== 'all') {
      if (filterUsCitizen === 'yes' && app.is_us_citizen !== 'yes') return false;
      if (filterUsCitizen === 'no' && app.is_us_citizen === 'yes') return false;
    }
    
    // Filter by verified status
    if (filterVerified !== 'all') {
      if (filterVerified === 'yes' && app.verified !== 1) return false;
      if (filterVerified === 'no' && app.verified === 1) return false;
    }
    
    // Search term - only apply client-side if NOT in "search all data" mode
    // When searchAllData is true, the search is done server-side
    if (searchTerm && !searchAllData) {
      const term = searchTerm.toLowerCase();
      return (
        app.name.toLowerCase().includes(term) ||
        app.email.toLowerCase().includes(term) ||
        app.position.toLowerCase().includes(term) ||
        (app.position_id && app.position_id.toLowerCase().includes(term))
      );
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="admin-applications">
        <div className="admin-container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-applications">
        <div className="admin-container">
          <div className="login-form-container">
            <h1>Admin Login</h1>
            <p className="login-subtitle">Enter password to view applications</p>
            
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                  autoFocus
                />
              </div>
              
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
              
              <button type="submit" className="login-button">
                Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-applications">
      <div className="admin-container">
        <div className="admin-header">
          <div>
            <h1>Career Applications</h1>
            <p className="applications-count">
              Showing {filteredApplications.length} of {applications.length} applications
              {dateRangeDays !== null && dateRangeDays !== 'all' && ` (Last ${dateRangeDays} days)`}
              <br />
              <span className="total-count">Total in database: {totalApplicationsCount}</span>
            </p>
          </div>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>

        {/* All Filters in One Row */}
        <div className="filters-container-unified">
          {/* Date Range */}
          <div className="filter-group">
            <label>📅 Show data from last:</label>
            <select
              value={dateRangeDays === null ? 'all' : dateRangeDays}
              onChange={(e) => {
                const value = e.target.value === 'all' ? null : parseInt(e.target.value);
                setDateRangeDays(value);
                const token = localStorage.getItem('admin_token');
                if (token) {
                  fetchApplications(token, value, '');
                }
              }}
              className="filter-select"
            >
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={15}>15 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value="all">All time</option>
            </select>
          </div>

          {/* Search with Toggle */}
          <div className="filter-group search-group-unified">
            <label>Search:</label>
            <div className="search-with-toggle">
              <input
                type="text"
                placeholder={searchAllData ? "Searching all records..." : "Search visible records..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`search-input ${loadingApplications && searchAllData ? 'searching' : ''}`}
              />
              <label className="toggle-label-inline">
                <input
                  type="checkbox"
                  checked={!searchAllData}
                  onChange={(e) => {
                    setSearchAllData(!e.target.checked);
                    if (e.target.checked) {
                      // Switched to quick search - reload with date filter only
                      const token = localStorage.getItem('admin_token');
                      if (token) {
                        fetchApplications(token, dateRangeDays, '');
                      }
                    }
                  }}
                />
                <span className="toggle-text">
                  {!searchAllData ? '⚡ Quick' : '🔍 All records'}
                </span>
              </label>
            </div>
          </div>

          {/* US Citizen */}
          <div className="filter-group">
            <label>US Citizen:</label>
            <select
              value={filterUsCitizen}
              onChange={(e) => setFilterUsCitizen(e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          {/* Work Authorization */}
          <div className="filter-group">
            <label>Work Authorization:</label>
            <select
              value={filterWorkAuth}
              onChange={(e) => setFilterWorkAuth(e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              {getWorkAuthTypes().map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Verified Filter */}
          <div className="filter-group">
            <label>Verified:</label>
            <select
              value={filterVerified}
              onChange={(e) => setFilterVerified(e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          {(searchTerm || filterUsCitizen !== 'all' || filterWorkAuth !== 'all' || filterVerified !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterUsCitizen('all');
                setFilterWorkAuth('all');
                setFilterVerified('all');
                if (searchAllData) {
                  const token = localStorage.getItem('admin_token');
                  if (token) {
                    fetchApplications(token, dateRangeDays, '');
                  }
                }
              }}
              className="clear-filters-btn"
            >
              Clear Filters
            </button>
          )}
        </div>

        {loadingApplications ? (
          <div className="loading-spinner">Loading applications...</div>
        ) : filteredApplications.length === 0 ? (
          <div className="no-applications">
            <p>{applications.length === 0 ? 'No applications found.' : 'No applications match the current filters.'}</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="applications-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Job ID</th>
                  <th>Name</th>
                  <th>Position</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>US Citizen</th>
                  <th>Work Auth</th>
                  <th>Verified</th>
                  <th>Resume</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map((app) => (
                  <tr
                    key={app.id}
                    className="table-row"
                  >
                    <td className="date-cell" onClick={() => setSelectedApplication(app)}>{formatDate(app.submission_date)}</td>
                    <td className="job-id-cell" onClick={() => setSelectedApplication(app)}>
                      {app.position_id ? (
                        <span className="job-id-badge">{app.position_id}</span>
                      ) : (
                        <span className="no-job-id">-</span>
                      )}
                    </td>
                    <td className="name-cell" onClick={() => setSelectedApplication(app)}>{app.name}</td>
                    <td className="position-cell" onClick={() => setSelectedApplication(app)}>
                      {app.position}
                    </td>
                    <td className="email-cell" onClick={() => setSelectedApplication(app)}>{app.email}</td>
                    <td className="phone-cell" onClick={(e) => e.stopPropagation()}>
                      <a href={`tel:${app.phone}`} className="phone-link">{app.phone}</a>
                    </td>
                    <td className="citizen-cell" onClick={() => setSelectedApplication(app)}>
                      {app.is_us_citizen === 'yes' ? (
                        <span className="badge-yes">Yes</span>
                      ) : app.is_us_citizen === 'no' ? (
                        <span className="badge-no">No</span>
                      ) : (
                        <span className="badge-na">N/A</span>
                      )}
                    </td>
                    <td className="work-auth-cell" onClick={() => setSelectedApplication(app)}>
                      {formatWorkAuth(app.work_authorization)}
                    </td>
                    <td className="verified-cell" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={app.verified === 1}
                        onChange={() => handleToggleVerified(app.id, app.verified)}
                        className="verified-checkbox"
                        title={app.verified === 1 ? "Verified" : "Not verified"}
                      />
                    </td>
                    <td className="resume-cell" onClick={(e) => e.stopPropagation()}>
                      {app.resume_filename ? (
                        <button
                          className="table-download-btn"
                          onClick={() => handleDownloadResume(app.id, app.resume_filename)}
                          title="Download Resume"
                        >
                          📥
                        </button>
                      ) : (
                        <span className="no-resume">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedApplication && (
        <div className="modal-overlay" onClick={() => setSelectedApplication(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedApplication(null)}>
              ×
            </button>

            <div className="modal-header">
              <h2>{selectedApplication.name}</h2>
              <p className="modal-position">{selectedApplication.position}</p>
              {selectedApplication.position_id && (
                <span className="position-id">ID: {selectedApplication.position_id}</span>
              )}
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <h3>Contact Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <strong>Email:</strong>
                    <a href={`mailto:${selectedApplication.email}`}>{selectedApplication.email}</a>
                  </div>
                  <div className="info-item">
                    <strong>Phone:</strong>
                    <a href={`tel:${selectedApplication.phone}`}>{selectedApplication.phone}</a>
                  </div>
                  {selectedApplication.linkedin_profile && (
                    <div className="info-item full-width">
                      <strong>LinkedIn:</strong>
                      <a href={selectedApplication.linkedin_profile} target="_blank" rel="noopener noreferrer">
                        {selectedApplication.linkedin_profile}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-section">
                <h3>Work Authorization</h3>
                <div className="info-grid">
                  {selectedApplication.is_us_citizen && (
                    <div className="info-item">
                      <strong>US Citizen:</strong>
                      <span className={selectedApplication.is_us_citizen === 'yes' ? 'badge-yes' : 'badge-no'}>
                        {selectedApplication.is_us_citizen === 'yes' ? 'Yes' : 'No'}
                      </span>
                    </div>
                  )}
                  {selectedApplication.work_authorization && (
                    <div className="info-item">
                      <strong>Work Authorization:</strong>
                      <span>{selectedApplication.work_authorization}</span>
                    </div>
                  )}
                  {selectedApplication.available_start_date && (
                    <div className="info-item">
                      <strong>Available Start Date:</strong>
                      <span>{selectedApplication.available_start_date}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedApplication.resume_filename && (
                <div className="modal-section">
                  <h3>Resume</h3>
                  <div className="resume-info">
                    <div className="info-item">
                      <strong>Filename:</strong>
                      <span>{selectedApplication.resume_filename}</span>
                      {selectedApplication.resume_size && (
                        <span className="file-size">
                          ({(selectedApplication.resume_size / 1024).toFixed(2)} KB)
                        </span>
                      )}
                    </div>
                    <button 
                      className="download-btn"
                      onClick={() => handleDownloadResume(selectedApplication.id, selectedApplication.resume_filename)}
                    >
                      📥 Download Resume
                    </button>
                  </div>
                </div>
              )}

              {selectedApplication.cover_letter && (
                <div className="modal-section">
                  <h3>Cover Letter</h3>
                  <div className="cover-letter-content">
                    {selectedApplication.cover_letter}
                  </div>
                </div>
              )}

              <div className="modal-section">
                <h3>Submission Details</h3>
                <div className="info-item">
                  <strong>Submitted:</strong>
                  <span>{formatDate(selectedApplication.submission_date)}</span>
                </div>
                {selectedApplication.email_id && (
                  <div className="info-item">
                    <strong>Email ID:</strong>
                    <span>{selectedApplication.email_id}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApplications;

